import { useTranslation } from 'react-i18next';

import React, { useMemo, useRef } from 'react';

import {
  Code,
  Link as LinkIcon,
  ListBullets,
  ListNumbers,
  TextB,
  // TextH,
  TextItalic,
  TextUnderline,
} from '@thinkific/chalk/react-icons';
import { Popover } from '@thinkific/chalk/recipes/Popover';

import type { Editor } from 'components/new/Posts/hooks/useEditor/useEditor';

import LinkForm from '../../hooks/useEditor/Extensions/Link/LinkForm';
import ActionButton from '../ActionButton';
import * as css from './MenuBar.css';

export interface MenuBarProps {
  editor?: Editor;
  menuAccessories?: (editor: Editor) => React.ReactNode;
  showToolbar?: boolean;
  onActionClick: (action: string) => void;
}

const MenuBar = ({ editor, menuAccessories, showToolbar, onActionClick }: MenuBarProps) => {
  const extensions = useMemo(() => {
    return editor?.options.extensions.map((extension) => extension.name);
  }, [editor]);
  const isReady = !!editor;
  const { t } = useTranslation();

  const linkRef = useRef<HTMLButtonElement>(null);

  const handleActionClick = (action: string, callback: () => void) => {
    onActionClick(action);
    callback();
  };

  const closePopover = () => {
    linkRef.current?.click();
  };

  return (
    <div
      aria-hidden={!showToolbar}
      className={css.container[showToolbar ? 'withToolbar' : 'withoutToolbar']}
    >
      <div className={css.leftContent} data-visible={showToolbar}>
        {/* {extensions?.includes('heading') && (
          <ActionButton
            arialLabel={t(
              'community.documentEditor.menuBar.headingSize1',
              'heading'
            )}
            disabled={!isReady}
            icon={TextH}
            isActive={isReady && editor.isActive('heading', { level: 2 })}
            onClick={() =>
              editor?.chain().focus().toggleHeading({ level: 2 }).run()
            }
          />
        )} */}
        {extensions?.includes('bold') && (
          <ActionButton
            arialLabel={t('community.documentEditor.menuBar.bold', 'bold')}
            dataQa="post-editor__bold"
            disabled={!isReady || !editor.can().chain().focus().toggleBold().run()}
            icon={TextB}
            isActive={isReady && editor.isActive('bold')}
            onClick={() =>
              handleActionClick('bold', () => editor?.chain().focus().toggleBold().run())
            }
          />
        )}
        {extensions?.includes('italic') && (
          <ActionButton
            arialLabel={t('community.documentEditor.menuBar.italic', 'italic')}
            dataQa="post-editor__italic"
            disabled={!isReady || !editor.can().chain().focus().toggleItalic().run()}
            icon={TextItalic}
            isActive={isReady && editor.isActive('italic')}
            onClick={() =>
              handleActionClick('italic', () => editor?.chain().focus().toggleItalic().run())
            }
          />
        )}
        {extensions?.includes('underline') && (
          <ActionButton
            arialLabel={t('community.documentEditor.menuBar.underline', 'underline')}
            dataQa="post-editor__underline"
            disabled={!isReady || !editor.can().chain().focus().toggleUnderline().run()}
            icon={TextUnderline}
            isActive={isReady && editor.isActive('underline')}
            onClick={() =>
              handleActionClick('underline', () => editor?.chain().focus().toggleUnderline().run())
            }
          />
        )}
        {extensions?.includes('bulletList') && (
          <ActionButton
            arialLabel={t('community.documentEditor.menuBar.bulletList', 'bullet list')}
            dataQa="post-editor__bullet-list"
            disabled={!isReady}
            icon={ListBullets}
            isActive={isReady && editor.isActive('bulletList')}
            onClick={() =>
              handleActionClick('bulletList', () =>
                editor?.chain().focus().toggleBulletList().run()
              )
            }
          />
        )}
        {extensions?.includes('orderedList') && (
          <ActionButton
            arialLabel={t('community.documentEditor.menuBar.orderedList', 'ordered list')}
            dataQa="post-editor__ordered-list"
            disabled={!isReady}
            icon={ListNumbers}
            isActive={isReady && editor.isActive('orderedList')}
            onClick={() =>
              handleActionClick('orderedList', () =>
                editor?.chain().focus().toggleOrderedList().run()
              )
            }
          />
        )}
        {extensions?.includes('code') && (
          <ActionButton
            arialLabel={t('community.documentEditor.menuBar.code', 'code')}
            dataQa="post-editor__code"
            disabled={!isReady}
            icon={Code}
            isActive={isReady && editor.isActive('code')}
            onClick={() =>
              handleActionClick('code', () => editor?.chain().focus().toggleCode().run())
            }
          />
        )}
        {extensions?.includes('link') && (
          <div className={css.linkButtonContainer}>
            <Popover
              title={t('community.documentEditor.menuBar.linkPopupDescription', 'Add Link')}
              trigger={
                <ActionButton
                  arialLabel={t('community.documentEditor.menuBar.link', 'link')}
                  dataQa="post-editor__link"
                  disabled={!isReady}
                  icon={LinkIcon}
                  isActive={isReady && editor.isActive('link')}
                  ref={linkRef}
                  onClick={() => handleActionClick('link', () => editor?.chain().focus().run())}
                />
              }
              triggerRef={linkRef}
              hiddenTitle
            >
              <LinkForm editor={editor} onChange={closePopover} />
            </Popover>
          </div>
        )}
        {menuAccessories && menuAccessories(editor)}
      </div>
    </div>
  );
};

export default MenuBar;
